import { useMemo, useRef, useState } from 'react';
import type { KoStage } from '@punditxi/shared';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

export interface BracketTreeTeam {
  id: number;
  name: string;
  groupLabel: string | null;
}

export interface KoPickValue {
  stage: KoStage;
  slotId: string;
  teamId: number | null;
}

interface Props {
  teams: BracketTreeTeam[];
  picks: KoPickValue[];
  onChange(stage: KoStage, slotId: string, teamId: number | null): void;
  locked?: boolean;
}

// Round metadata: number of slots per stage and visual width per column.
const ROUNDS: { stage: KoStage; label: string; count: number }[] = [
  { stage: 'R32', label: 'R32', count: 16 },
  { stage: 'R16', label: 'R16', count: 8 },
  { stage: 'QF', label: 'QF', count: 4 },
  { stage: 'SF', label: 'SF', count: 2 },
  { stage: 'FINAL', label: 'Final', count: 1 },
];

// Each card is ~72px tall. R32 stacks 16 cards with a 4px gap → ~1216px.
// Subsequent rounds use proportionally larger gaps so that each slot's
// vertical centre aligns with the midpoint of its two inputs in the prior
// round. Implemented via flex justify-around inside a shared-height container.

const TOTAL_HEIGHT_PX = 16 * 72 + 15 * 4; // 1212px — used as the shared column height

export function BracketTree({ teams, picks, onChange, locked }: Props) {
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const pickByKey = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const p of picks) m.set(`${p.stage}:${p.slotId}`, p.teamId);
    return m;
  }, [picks]);

  const [openSlot, setOpenSlot] = useState<{ stage: KoStage; slotId: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Resolve the candidate teams that can be picked for a given slot:
   *  - R32: every WC qualifier (48 teams) — pre-WC we don't model the draw path
   *    so user picks from the full roster.
   *  - R16+: winner of the two upstream R32 slots, when those exist. Otherwise
   *    no candidates yet → "Pick winners earlier in the bracket first".
   */
  function candidatesFor(stage: KoStage, slotId: string): BracketTreeTeam[] {
    if (stage === 'R32') return teams;
    const upstream = upstreamSlots(stage, slotId);
    const upstreamTeamIds = upstream
      .map((u) => pickByKey.get(`${u.stage}:${u.slotId}`))
      .filter((id): id is number => typeof id === 'number');
    return upstreamTeamIds
      .map((id) => teamById.get(id))
      .filter((t): t is BracketTreeTeam => !!t);
  }

  const showCandidates = openSlot ? candidatesFor(openSlot.stage, openSlot.slotId) : [];

  return (
    <div className="space-y-3">
      {/* Sticky round-label row */}
      <div className="sticky top-0 z-10 bg-bg-night/95 backdrop-blur-sm -mx-4 px-4 py-2">
        <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
          {ROUNDS.map((r) => (
            <div
              key={r.stage}
              className="font-display-alt text-[10px] uppercase tracking-widest text-trophy-gold"
              style={{ width: 140 }}
            >
              {r.label}
            </div>
          ))}
        </div>
      </div>

      {/* Horizontally-scrolling tree */}
      <div
        ref={containerRef}
        className="overflow-x-auto -mx-4 px-4 pb-4"
        style={{ scrollbarWidth: 'none' }}
      >
        <div
          className="relative flex gap-3"
          style={{ height: TOTAL_HEIGHT_PX, minWidth: 'max-content' }}
        >
          {/* SVG connector lines layer — sits behind the slot cards. */}
          <ConnectorLayer pickByKey={pickByKey} />
          {ROUNDS.map((r) => (
            <div
              key={r.stage}
              className="relative flex flex-col justify-around"
              style={{ width: 140, height: TOTAL_HEIGHT_PX }}
            >
              {Array.from({ length: r.count }, (_, i) => {
                const slotId = `${r.stage}-M${i + 1}`;
                const teamId = pickByKey.get(`${r.stage}:${slotId}`) ?? null;
                const team = teamId !== null ? teamById.get(teamId) : null;
                const isOpen = openSlot?.stage === r.stage && openSlot.slotId === slotId;
                return (
                  <SlotCard
                    key={slotId}
                    label={slotId.replace(/-M/, ' #')}
                    team={team ?? null}
                    isOpen={isOpen}
                    onClick={() => {
                      if (locked) return;
                      haptic.tap();
                      if (isOpen) {
                        setOpenSlot(null);
                      } else {
                        setOpenSlot({ stage: r.stage, slotId });
                      }
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Picker sheet (inline below the tree) */}
      {openSlot && (
        <div className="rounded-xl border border-trophy-gold/40 bg-bg-card p-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold">
                Pick winner — {openSlot.stage} {openSlot.slotId.replace(/.*-M/, '#')}
              </div>
              <div className="text-xs text-text-dim mt-0.5">
                {openSlot.stage === 'R32'
                  ? `${teams.length} teams in the running`
                  : showCandidates.length === 0
                    ? 'Pick winners in earlier rounds first to populate this match.'
                    : `${showCandidates.length} team${showCandidates.length === 1 ? '' : 's'} to choose from`}
              </div>
            </div>
            <button
              onClick={() => setOpenSlot(null)}
              className="text-text-faint text-xs px-2"
              aria-label="Close picker"
            >
              ✕
            </button>
          </div>

          {showCandidates.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto">
              {showCandidates.map((t) => {
                const selected = pickByKey.get(`${openSlot.stage}:${openSlot.slotId}`) === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      haptic.select();
                      onChange(openSlot.stage, openSlot.slotId, selected ? null : t.id);
                    }}
                    className={cn(
                      'rounded-md px-2 py-2 text-left text-xs font-semibold border transition-colors',
                      selected
                        ? 'bg-trophy-gold border-trophy-gold text-bg-night'
                        : 'bg-bg-elevated text-text-base border-border-base',
                    )}
                  >
                    <div className="truncate">{t.name}</div>
                    {t.groupLabel && (
                      <div
                        className={cn(
                          'font-mono text-[9px] mt-0.5',
                          selected ? 'text-bg-night/70' : 'text-text-faint',
                        )}
                      >
                        Group {t.groupLabel}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-text-faint text-center">
        Swipe horizontally to see all rounds. Tap any slot to pick the winner.
      </div>
    </div>
  );
}

const COL_WIDTH = 140;
const COL_GAP = 12;
const SLOT_HEIGHT = 64;

/**
 * Compute the vertical centre (in px) of slot index `i` within a column that
 * lays out `n` cards with `justify-around` inside `TOTAL_HEIGHT_PX`. This
 * mirrors the flex math browsers do so connector lines land exactly on slot
 * midpoints.
 */
function slotCenterY(i: number, n: number): number {
  const freeSpace = TOTAL_HEIGHT_PX - n * SLOT_HEIGHT;
  const perGap = freeSpace / n;
  const halfGap = perGap / 2;
  return halfGap + i * (SLOT_HEIGHT + perGap) + SLOT_HEIGHT / 2;
}

function ConnectorLayer({
  pickByKey,
}: {
  pickByKey: Map<string, number | null>;
}) {
  const totalWidth = ROUNDS.length * COL_WIDTH + (ROUNDS.length - 1) * COL_GAP;

  // For each pair of adjacent rounds (i, i+1), draw 2 curves per downstream slot.
  const paths: { d: string; key: string; lit: boolean }[] = [];
  for (let r = 0; r < ROUNDS.length - 1; r++) {
    const left = ROUNDS[r]!;
    const right = ROUNDS[r + 1]!;
    const leftColRightX = r * (COL_WIDTH + COL_GAP) + COL_WIDTH;
    const rightColLeftX = (r + 1) * (COL_WIDTH + COL_GAP);
    for (let j = 0; j < right.count; j++) {
      const upstreamA = j * 2;
      const upstreamB = j * 2 + 1;
      const aY = slotCenterY(upstreamA, left.count);
      const bY = slotCenterY(upstreamB, left.count);
      const dY = slotCenterY(j, right.count);

      // Lit if the downstream slot's team matches either upstream pick
      // (i.e. the chosen winner advanced from one of these two).
      const downstreamTeam = pickByKey.get(`${right.stage}:${right.stage}-M${j + 1}`);
      const upstreamATeam = pickByKey.get(`${left.stage}:${left.stage}-M${upstreamA + 1}`);
      const upstreamBTeam = pickByKey.get(`${left.stage}:${left.stage}-M${upstreamB + 1}`);
      const litA = downstreamTeam !== null && downstreamTeam === upstreamATeam;
      const litB = downstreamTeam !== null && downstreamTeam === upstreamBTeam;

      // Two bezier curves into the downstream slot.
      const cpOffset = 30;
      paths.push({
        key: `${r}-${j}-A`,
        lit: litA,
        d: `M ${leftColRightX} ${aY} C ${leftColRightX + cpOffset} ${aY}, ${rightColLeftX - cpOffset} ${dY}, ${rightColLeftX} ${dY}`,
      });
      paths.push({
        key: `${r}-${j}-B`,
        lit: litB,
        d: `M ${leftColRightX} ${bY} C ${leftColRightX + cpOffset} ${bY}, ${rightColLeftX - cpOffset} ${dY}, ${rightColLeftX} ${dY}`,
      });
    }
  }

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={totalWidth}
      height={TOTAL_HEIGHT_PX}
      viewBox={`0 0 ${totalWidth} ${TOTAL_HEIGHT_PX}`}
      aria-hidden
    >
      {paths.map((p) => (
        <path
          key={p.key}
          d={p.d}
          fill="none"
          stroke={p.lit ? '#F5C842' : 'rgba(36,48,86,0.6)'}
          strokeWidth={p.lit ? 2 : 1.5}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

function SlotCard({
  label,
  team,
  isOpen,
  onClick,
}: {
  label: string;
  team: BracketTreeTeam | null;
  isOpen: boolean;
  onClick(): void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col justify-between rounded-md border px-2 py-1.5 text-left transition-colors h-[64px]',
        isOpen
          ? 'border-trophy-gold bg-bg-elevated'
          : team
            ? 'border-trophy-gold/40 bg-bg-card'
            : 'border-border-base bg-bg-card',
      )}
    >
      <div className="font-display-alt text-[9px] uppercase tracking-widest text-text-faint">
        {label}
      </div>
      {team ? (
        <>
          <div className="font-chunky text-xs truncate text-text-base">{team.name}</div>
          {team.groupLabel && (
            <div className="font-mono text-[9px] text-text-faint">Group {team.groupLabel}</div>
          )}
        </>
      ) : (
        <div className="text-[10px] text-text-faint">Tap to pick</div>
      )}
    </button>
  );
}

/**
 * For a knockout slot in R16/QF/SF/FINAL, return the two upstream slot IDs
 * whose winners feed into it.
 *  - R16-M1 ← R32-M1, R32-M2
 *  - R16-M2 ← R32-M3, R32-M4
 *  - ...
 *  - QF-M1 ← R16-M1, R16-M2
 *  - ...
 */
function upstreamSlots(stage: KoStage, slotId: string): { stage: KoStage; slotId: string }[] {
  const i = Number.parseInt(slotId.split('-M')[1] ?? '0', 10);
  if (!Number.isFinite(i) || i < 1) return [];
  const prior: KoStage =
    stage === 'R16' ? 'R32' : stage === 'QF' ? 'R16' : stage === 'SF' ? 'QF' : 'SF';
  return [
    { stage: prior, slotId: `${prior}-M${i * 2 - 1}` },
    { stage: prior, slotId: `${prior}-M${i * 2}` },
  ];
}
